import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/apiClient";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import PilHero from "@/components/PilHero";
import DocRecord from "@/components/DocRecord";
import CoverageList from "@/components/CoverageList";
import ShareButton, { Disclaimer } from "@/components/Shared";
import SocialShare from "@/components/SocialShare";

function Section({ eyebrow, title, children }) {
  return (
    <section className="py-10 border-t border-npst-line" data-testid={`section-${(eyebrow || title).toLowerCase().replace(/\s/g, "-")}`}>
      <div className="label-eyebrow text-npst-gold mb-4">{eyebrow || title}</div>
      {children}
    </section>
  );
}

export default function PilDetail() {
  const { code } = useParams();
  const s = useSettings();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/pils/${code}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [code]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <div className="font-serif text-7xl text-npst-goldBorder">404</div>
      <h1 className="font-serif text-3xl text-npst-ink mt-4">Matter not found</h1>
      <p className="text-npst-muted mt-3">This PIL is unavailable or has not been published.</p>
      <Link to="/pil" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy">← Back to Archive</Link>
    </div>
  );
  if (!data) return <div className="py-32 text-center text-npst-muted animate-pulse">Loading matter…</div>;

  const { pil, orders, documents, timeline, press_releases, initiatives, media_coverage } = data;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "LegalCase", name: pil.title,
    description: pil.cause_of_action, identifier: pil.pil_code,
  };

  return (
    <div className="fade-in">
      <SEO route={`/pil/${pil.pil_code.toLowerCase()}`} title={`${pil.title} — ${pil.pil_code}`} description={(pil.meta_description || pil.cause_of_action || "").slice(0, 160)} canonical={`${window.location.origin}/pil/${pil.pil_code.toLowerCase()}`} jsonLd={jsonLd} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
        <button onClick={() => navigate("/pil")} className="inline-flex items-center gap-2 text-sm text-npst-muted hover:text-npst-navy transition-colors" data-testid="back-to-archive"><ArrowLeft size={15} /> Public Interest Litigations</button>
        <ShareButton title={pil.title} />
      </div>
      {pil.state === "published" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 flex justify-end">
          <SocialShare prefix="pil" url={`${process.env.REACT_APP_BACKEND_URL}/api/share/pils/${pil.pil_code.toLowerCase()}`} text={`${pil.title} — ${pil.pil_code}, National Public Service Trust`} />
        </div>
      )}

      {pil.state && pil.state !== "published" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-md font-semibold uppercase tracking-wide">Preview · {pil.state}</div>
        </div>
      )}

      <PilHero pil={pil} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 lg:col-start-5">
            {pil.background && (
              <Section eyebrow="Case Overview">
                <div className="prose-npst"><p className="font-sans">{pil.background}</p></div>
              </Section>
            )}
            {pil.principal_issue && (
              <Section eyebrow="Issues Raised">
                <p className="font-serif text-xl text-npst-ink leading-relaxed">{pil.principal_issue}</p>
                {pil.statutory_provisions?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {pil.statutory_provisions.map((sp, i) => <span key={i} className="text-xs font-mono px-2.5 py-1 bg-npst-navy/5 text-npst-navy border border-npst-navy/10 rounded">{sp}</span>)}
                  </div>
                )}
              </Section>
            )}
            {pil.reliefs_sought?.length > 0 && (
              <Section eyebrow="Reliefs Sought">
                <ul className="space-y-3">
                  {pil.reliefs_sought.map((r, i) => (
                    <li key={i} className="flex gap-3 text-npst-inkSoft leading-relaxed"><span className="font-mono text-npst-gold text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span><span>{r}</span></li>
                  ))}
                </ul>
              </Section>
            )}
            {timeline?.length > 0 && (
              <Section eyebrow="Case Timeline">
                <div className="space-y-0">
                  {timeline.map((t) => (
                    <div key={t.id} className="relative pl-8 pb-8 border-l border-npst-line last:border-transparent" data-testid="timeline-event">
                      <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-npst-gold" />
                      <div className="text-[11px] font-sans font-bold uppercase tracking-[0.18em] text-npst-gold">{t.event_date}</div>
                      <div className="font-serif text-xl text-npst-ink mt-1 leading-snug">{t.title}</div>
                      {t.description && <p className="text-sm text-npst-inkSoft mt-1 leading-relaxed">{t.description}</p>}
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {orders?.length > 0 && (
              <Section eyebrow="Orders & Case Documents">
                <div>{orders.map((o) => <DocRecord key={o.id} doc={o} />)}</div>
              </Section>
            )}
            {documents?.length > 0 && (
              <Section eyebrow="Related Documents">
                <div>{documents.map((d) => <DocRecord key={d.id} doc={d} />)}</div>
              </Section>
            )}
            {press_releases?.length > 0 && (
              <Section eyebrow="Related Press Releases">
                <div className="space-y-4">
                  {press_releases.map((p) => (
                    <Link key={p.id} to={`/media/${p.slug}`} className="block group">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-npst-gold font-semibold">{p.publish_date} · {p.category}</div>
                      <div className="font-serif text-xl text-npst-ink group-hover:text-npst-navy transition-colors">{p.headline}</div>
                    </Link>
                  ))}
                </div>
              </Section>
            )}
            {initiatives?.length > 0 && (
              <Section eyebrow="Related Initiatives">
                <div className="space-y-4">
                  {initiatives.map((it) => (
                    <Link key={it.id} to={`/initiatives/${it.slug}`} className="block group">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-npst-gold font-semibold">{it.category}</div>
                      <div className="font-serif text-xl text-npst-ink group-hover:text-npst-navy transition-colors">{it.title}</div>
                    </Link>
                  ))}
                </div>
              </Section>
            )}
            {media_coverage?.length > 0 && (
              <Section eyebrow="In the Media">
                <CoverageList items={media_coverage} />
              </Section>
            )}
            <Disclaimer text={s.disclaimer} />
          </div>
        </div>
      </div>
    </div>
  );
}
